// src/Components/Modules/HR/NotificationCenter.js
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, IconButton, Badge, Popover, Stack, Chip, Divider, Tooltip, Button,
} from "@mui/material";
import {
  FaBell, FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaCalendarAlt,
  FaFileInvoiceDollar, FaFingerprint, FaInbox, FaCheckDouble,
} from "react-icons/fa";

const LS_KEY = "hr_read_notifications";

const SEVERITY = {
  error:   { bg: "#fef2f2", color: "#dc2626" },
  success: { bg: "#f0fdf4", color: "#16a34a" },
  warning: { bg: "#fffbeb", color: "#d97706" },
  info:    { bg: "#eff6ff", color: "#2563eb" },
};

const TYPE_ICON = {
  rejected:   FaTimesCircle,
  approved:   FaCheckCircle,
  pending:    FaHourglassHalf,
  leave:      FaCalendarAlt,
  payslip:    FaFileInvoiceDollar,
  attendance: FaFingerprint,
};

const timeAgo = (dateStr) => {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (isNaN(diff)) return "";
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const NotificationCenter = ({ requests = [], leaveBalance = [], payslips = [], todayHistory = [] }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [tab, setTab] = useState("all");
  const [readIds, setReadIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(LS_KEY) || "[]");
    } catch {
      return [];
    }
  });

  const persistRead = (ids) => {
    const capped = ids.slice(-200); // prevent unbounded growth
    setReadIds(capped);
    localStorage.setItem(LS_KEY, JSON.stringify(capped));
  };

  // ---- Build notifications from live dashboard data (no new API needed) ----
  const notifications = useMemo(() => {
    const list = [];
    const now = new Date();

    // 1) Request lifecycle (leave / expense / loan / requisition)
    (requests || []).forEach((r) => {
      const base = { date: r.date, link: "/hr/my-requests" };
      if (r.status === "rejected") {
        list.push({
          ...base, id: `rej-${r.type}-${r.id}`, kind: "rejected", severity: "error",
          title: `${r.type} request rejected`,
          message: r.rejectionReason ? `${r.details} — Reason: ${r.rejectionReason}` : r.details,
        });
      } else if (["approved", "fulfilled", "repaying", "closed"].includes(r.status)) {
        list.push({
          ...base, id: `app-${r.type}-${r.id}`, kind: "approved", severity: "success",
          title: `${r.type} request approved`, message: r.details,
        });
      } else if (r.status === "pending") {
        list.push({
          ...base, id: `pen-${r.type}-${r.id}`, kind: "pending", severity: "warning",
          title: `${r.type} awaiting approval`, message: r.details,
        });
      }
    });

    // 2) Low leave balance (≤ 20% remaining)
    (leaveBalance || []).forEach((l) => {
      if (l.allocated > 0 && l.remaining / l.allocated <= 0.2) {
        list.push({
          id: `leave-low-${l.leave_type_id}`, kind: "leave", severity: "warning",
          title: "Low leave balance",
          message: `Only ${l.remaining} of ${l.allocated} ${l.name || "leave"} days remaining`,
          date: now.toISOString(), link: "/hr/leave/apply",
        });
      }
    });

    // 3) Payslips ready
    (payslips || []).slice(0, 3).forEach((p) => {
      list.push({
        id: `payslip-${p.id}`, kind: "payslip", severity: "info",
        title: "Payslip available",
        message: `${p.period?.period_name || "Your payslip"} is ready to download`,
        date: p.created_at || now.toISOString(), link: "/hr/payslips",
      });
    });

    // 4) Attendance reminder (after 9 AM, no punch yet)
    if ((todayHistory || []).length === 0 && now.getHours() >= 9) {
      list.push({
        id: `punch-${now.toDateString()}`, kind: "attendance", severity: "warning",
        title: "Attendance reminder",
        message: "You haven't punched in yet today",
        date: now.toISOString(), link: null,
      });
    }

    return list.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 30);
  }, [requests, leaveBalance, payslips, todayHistory]);

  const unread = notifications.filter((n) => !readIds.includes(n.id));
  const visible = tab === "unread" ? unread : notifications;
  const open = Boolean(anchorEl);

  const handleClickItem = (n) => {
    persistRead([...readIds, n.id]);
    setAnchorEl(null);
    if (n.link) navigate(n.link);
  };

  const markAllRead = () =>
    persistRead([...new Set([...readIds, ...notifications.map((n) => n.id)])]);

  return (
    <>
      {/* ✅ Bell now shows UNREAD notification count */}
      <Tooltip title="Notifications">
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}
        >
          <Badge badgeContent={unread.length} color="error" max={99}>
            <FaBell />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 400, maxWidth: "95vw", borderRadius: 2,
            border: "1px solid #e2e8f0", overflow: "hidden",
            boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle1" fontWeight={700} color="#1e293b">
              Notifications
              {unread.length > 0 && (
                <Chip label={`${unread.length} new`} size="small"
                  sx={{ ml: 1, bgcolor: "#fee2e2", color: "#991b1b", fontWeight: 700, fontSize: 11 }} />
              )}
            </Typography>
            <Button size="small" startIcon={<FaCheckDouble />} onClick={markAllRead}
              disabled={unread.length === 0} sx={{ textTransform: "none", fontWeight: 600, color: "#6366f1" }}>
              Mark all read
            </Button>
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            {["all", "unread"].map((t) => (
              <Chip key={t} label={t === "all" ? "All" : "Unread"} size="small" onClick={() => setTab(t)}
                sx={{
                  textTransform: "capitalize", fontWeight: 600,
                  bgcolor: tab === t ? "#6366f1" : "#e2e8f0",
                  color: tab === t ? "white" : "#475569",
                }} />
            ))}
          </Stack>
        </Box>

        {/* List */}
        <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
          {visible.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, color: "#94a3b8" }}>
              <FaInbox size={40} style={{ marginBottom: 12, color: "#cbd5e1" }} />
              <Typography variant="subtitle2" color="#475569" fontWeight={600}>You're all caught up! 🎉</Typography>
              <Typography variant="caption">No notifications {tab === "unread" ? "to read" : "yet"}</Typography>
            </Box>
          ) : (
            visible.map((n) => {
              const Icon = TYPE_ICON[n.kind] || FaBell;
              const sev = SEVERITY[n.severity] || SEVERITY.info;
              const isUnread = !readIds.includes(n.id);
              return (
                <Box
                  key={n.id}
                  onClick={() => handleClickItem(n)}
                  sx={{
                    display: "flex", gap: 1.5, p: 1.75, cursor: "pointer",
                    borderBottom: "1px solid #f1f5f9",
                    bgcolor: isUnread ? "#f8faff" : "white",
                    "&:hover": { bgcolor: "#f1f5f9" },
                  }}
                >
                  <Box sx={{
                    width: 38, height: 38, borderRadius: 1.5, flexShrink: 0,
                    bgcolor: sev.bg, color: sev.color,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Icon size={16} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700} color="#1e293b" noWrap sx={{ flex: 1 }}>
                        {n.title}
                      </Typography>
                      {isUnread && <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#6366f1", flexShrink: 0 }} />}
                    </Box>
                    <Typography variant="body2" color="#64748b" sx={{
                      display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                    }}>
                      {n.message}
                    </Typography>
                    <Typography variant="caption" color="#94a3b8">{timeAgo(n.date)}</Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Box sx={{ p: 1.25, textAlign: "center", bgcolor: "#f8fafc" }}>
          <Typography variant="caption" color="#94a3b8">
            Generated from your requests, leaves, payslips & attendance
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationCenter;